import { HexColorPicker } from "react-colorful";
import { SidebarLayoutShowMobile } from "../../layout/SideBarLayout/SidebarLayout.styles";
import { theme } from "../../theme";
import { V3Dropdown } from "../Dropdown";
import { V3InputLabel } from "../SharedInputComponent";
import { V3Tooltip } from "../Tooltip";
import { V3Caption } from "../Typography";
import { VerticalSpacing } from "../VerticalSpacing";
import {
    ColorPickerDropdownWrapper,
    ColorPickerSwatch,
    ColorSwatchInputWrapper,
    ColorSwatchOptions,
    ColorSwatchTextInputWrapper,
    V3InputColorPreview
} from "./ColorSwatch.styles";
import { V3TextInput } from "../TextInput/V3TextInput";
import { FormikProps } from "formik";
import { FakeSelectStyle, FakeSelectStyleProps } from "../FakeSelect";
import { BACKGROUND_COLORS } from "../BrandingBar";

type WithColor = { color?: string | null };

interface ColorSwatchProps<FormValues extends WithColor> {
    formik: FormikProps<FormValues>;
    colors?: string[];
    onChange?: (color: string) => void;
    allowColorPicker?: boolean;
}

export function ColorSwatch<FormValues extends WithColor>({
    formik,
    colors = BACKGROUND_COLORS.slice(1),
    onChange,
    allowColorPicker = true
}: ColorSwatchProps<FormValues>) {
    const hasColorError = formik.errors.hasOwnProperty("color") && formik.touched.hasOwnProperty("color");

    return (
        <ColorSwatchTextInputWrapper>
            <div>
                <VerticalSpacing bottom={0} breakpoints={[{ screen: theme.v3.layout.sidebarLayout.mobile, marginBottom: 14 }]}>
                    <V3InputLabel>
                        <strong>Select a color:</strong>
                    </V3InputLabel>
                </VerticalSpacing>
                <ColorSwatchOptions>
                    {colors.map((presetColor) => (
                        <ColorPickerSwatch
                            key={presetColor}
                            className="picker__swatch"
                            style={{ background: presetColor }}
                            selected={formik.values.color === presetColor}
                            onClick={() => {
                                onChange && onChange(presetColor);
                                formik.setFieldValue("color", presetColor);
                            }}
                        />
                    ))}
                    {allowColorPicker && (
                        <div>
                            <V3Dropdown
                                renderReferenceElement={(open) => {
                                    const Swatch = () => (
                                        <ColorPickerSwatch
                                            style={{
                                                background: "linear-gradient(45deg, #FA8BFF 0%, #2BD2FF 52%, #2BFF88 90%)"
                                            }}
                                        />
                                    );

                                    if (!open) return <V3Tooltip tooltipContent={"Show color palette"} referenceContent={<Swatch />} />;
                                    return <Swatch />;
                                }}
                                renderPopperElement={() => {
                                    return (
                                        <ColorPickerDropdownWrapper>
                                            <SidebarLayoutShowMobile>
                                                <V3Caption>
                                                    <strong>Pick your color:</strong>
                                                </V3Caption>
                                            </SidebarLayoutShowMobile>
                                            <HexColorPicker
                                                color={formik.values.color?.toUpperCase()}
                                                onChange={(hxClr) => {
                                                    onChange && onChange(hxClr.toUpperCase());
                                                    formik.setFieldValue("color", hxClr.toUpperCase());
                                                }}
                                            />
                                        </ColorPickerDropdownWrapper>
                                    );
                                }}
                            />
                        </div>
                    )}
                </ColorSwatchOptions>
            </div>
            <ColorSwatchInputWrapper>
                <V3TextInput
                    label="...or enter HEX code:"
                    name="color"
                    placeholder="e.g. #AABBCC"
                    onChange={(e) => {
                        let raw = e.target.value.trim();
                        if (!raw.startsWith("#") && raw.length === 6) raw = `#${raw}`;
                        formik.setFieldValue("color", raw);
                    }}
                    onBlur={formik.handleBlur}
                    value={formik.values.color!}
                    error={hasColorError ? (formik.errors.color as string) : undefined}
                    icon={<V3InputColorPreview style={{ background: formik.values.color! }} />}
                    onKeyDown={(e) => {
                        if (e.key === "Enter" && formik.values.color) {
                            onChange && onChange(formik.values.color.toUpperCase());
                        }
                    }}
                />
            </ColorSwatchInputWrapper>
        </ColorSwatchTextInputWrapper>
    );
}

export const InputWithColorSwatch = ({ text, label, tooltipContent, tooltipMaxWidth }: FakeSelectStyleProps) => (
    <ColorSwatchInputWrapper>
        <FakeSelectStyle
            brandingBar
            label={label}
            hasContentSelected
            tooltipContent={tooltipContent}
            tooltipMaxWidth={tooltipMaxWidth}
            text={text}
            useHover
            icon={<V3InputColorPreview style={{ background: text! }} $largePreview />}
        />
    </ColorSwatchInputWrapper>
);
